import { Css, Palette, useTestIds } from "@homebound/beam";
import { MouseEvent } from "react";
import { Document, Page } from "react-pdf";
import { Icon } from "src/components/Icon";
import { AssetInfoFragment } from "src/routes/projects/assets/AssetPreview.generated";
type AssetPreviewProps = {
  onClick?: Function;
  asset: AssetInfoFragment;
  dimensions?: { width?: number; height?: number };
  mediaFit?: "contain" | "cover";
};

export function AssetPreview(props: AssetPreviewProps) {
  const { asset, onClick, dimensions, mediaFit } = props;
  const docType = asset.contentType;

  if (docType === "application/pdf") {
    return <PdfPreview asset={asset} onClick={onClick} dimensions={dimensions} />;
  } else if (docType.includes("video/")) {
    // Before the generic `image/` branch: videos have no resized `previewUrl` (resize is
    // image-only), so an `<img>` would render broken.
    return <VideoPreview asset={asset} onClick={onClick} dimensions={dimensions} mediaFit={mediaFit} />;
  } else if (docType.includes("image/")) {
    return <ImagePreview asset={asset} onClick={onClick} dimensions={dimensions} mediaFit={mediaFit} />;
  } else if (isDocumentType(docType)) {
    return <DocumentPreview asset={asset} onClick={onClick} dimensions={dimensions} />;
  } else {
    return <NoPreviewComponent dimensions={dimensions} />;
  }
}

function PdfPreview(props: Omit<AssetPreviewProps, "mediaFit">) {
  const { asset, onClick, dimensions } = props;

  return (
    <button
      onClick={(e) => {
        e.currentTarget.blur();
        onClick && onClick();
      }}
      css={{
        ...previewStyles,
        ...Css.if(!!dimensions?.width).wPx(dimensions?.width!).mwPx(dimensions?.width!).else.w100.$,
        ...Css.hPx(dimensions?.height ?? 208).mhPx(dimensions?.height ?? 208).$,
        ...Css.onFocus.add("outline", `1px ${Palette.Gray900} dashed`).$,
        ...Css.onHover.bshHover.$,
      }}
      data-testid="docThumbnail"
    >
      <Document file={asset.downloadUrl} loading="" renderMode="svg">
        <Page
          css={Css.maxwPx(250).maxhPx(200).$}
          height={dimensions?.height ?? 200}
          width={dimensions?.width}
          loading=""
          pageNumber={1}
          renderMode="canvas"
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
    </button>
  );
}

function ImagePreview(props: AssetPreviewProps) {
  const { asset, onClick, dimensions, mediaFit } = props;
  const { previewUrl, downloadUrl, contentType } = asset;
  const src = contentType === "image/gif" ? downloadUrl : previewUrl || downloadUrl;

  return (
    <img
      src={src}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }
      }}
      css={{
        ...previewStyles,
        ...Css.if(mediaFit === "cover").objectCover.else.objectContain.$,
        ...Css.if(!!dimensions?.width).wPx(dimensions?.width!).mwPx(dimensions?.width!).else.w100.$,
        ...Css.hPx(dimensions?.height ?? 208).mhPx(dimensions?.height ?? 208).$,
      }}
    />
  );
}

function VideoPreview(props: AssetPreviewProps) {
  const { asset, onClick, dimensions, mediaFit } = props;
  const { downloadUrl } = asset;
  const tid = useTestIds(props, "videoThumbnail");

  const handleClick = (e: MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }
  };

  const width = dimensions?.width;
  const height = dimensions?.height ?? 208;

  return (
    <button
      type="button"
      aria-label="Play video"
      onClick={handleClick}
      css={{
        ...Css.relative.df.aic.jcc.$,
        ...Css.if(!!width).wPx(width!).mwPx(width!).else.w100.$,
        ...Css.hPx(height).mhPx(height).$,
      }}
      {...tid}
    >
      {/* preload="metadata" surfaces the first frame as a poster (videos have no previewUrl). */}
      <video
        src={downloadUrl}
        preload="metadata"
        muted
        playsInline
        css={{
          ...previewStyles,
          ...Css.if(mediaFit === "cover").objectCover.else.objectContain.$,
          ...Css.if(!!width).wPx(width!).mwPx(width!).else.w100.$,
          ...Css.hPx(height).mhPx(height).$,
        }}
      />
      <PlayOverlay />
    </button>
  );
}

// A centered, semi-transparent play badge overlaid on a video thumbnail so videos are
// visually distinguishable from images.
function PlayOverlay() {
  const tid = useTestIds({}, "playOverlay");
  return (
    <div css={Css.absolute.df.aic.jcc.br100.bgColor("rgba(0,0,0,0.45)").wPx(40).hPx(40).pen.$} {...tid}>
      <Icon icon="play" color={Palette.White} inc={4} />
    </div>
  );
}

function DocumentPreview(props: Omit<AssetPreviewProps, "mediaFit">) {
  const { dimensions, onClick, asset } = props;

  return (
    <div
      css={{
        ...previewStyles,
        ...Css.if(!!dimensions?.width).wPx(dimensions?.width!).mwPx(dimensions?.width!).else.w100.$,
        ...Css.hPx(dimensions?.height ?? 80).mhPx(dimensions?.height ?? 80).$,
      }}
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }
      }}
    >
      <span css={Css.mr2.fs0.$}>
        <Icon icon="file" />
      </span>
      <div css={Css.oh.xs.$}>{asset.fileName || "No preview available"}</div>
    </div>
  );
}

function NoPreviewComponent(props: Pick<AssetPreviewProps, "dimensions">) {
  const { dimensions } = props;

  return (
    <div
      css={{
        ...previewStyles,
        ...Css.if(!!dimensions?.width).wPx(dimensions?.width!).mwPx(dimensions?.width!).else.w100.$,
        ...Css.hPx(dimensions?.height ?? 80).mhPx(dimensions?.height ?? 80).$,
      }}
    >
      <span css={Css.mr2.fs0.$}>
        <Icon icon="polaroids" />
      </span>
      <div>No preview available</div>
    </div>
  );
}
const previewStyles = Css.bn.br4.df.aic.jcc.bshBasic.cursorPointer.bgWhite.$;

// Determines if a given content type is a text document, excel document, or csv document
export function isDocumentType(contentType: string) {
  return contentType.includes("text/") || contentType.includes("spreadsheet") || contentType.includes("document");
}
